import React, { useContext, useState } from "react";
import {
  Button,
  Input,
  Form,
  AutoComplete,
  Tooltip,
} from "antd";
import { FormContext } from "./ChemPesticidesTableRow";
import { TEST_OVERDUE_FLAGS, getTestOverdueFlag } from "../../../../../utils/statusConfig";
import { mrlResultRule } from "../../Helper";
import "./ChemPesticidesTableCell.css";

const ChemPesticidesTableCell = ({
  title,
  children,
  dataIndex,
  record,
  openModal,
  sampleDetail,
  handleDeleteRow,
  handleCompoundChange,
  index,
  loading,
  compoundCounterMap,
  checkAndUpdateCompoundMap,
  ...antdProps
}) => {
  const form = useContext(FormContext);
  const [prevCompound, setPrevCompound] = useState(record?.compound);
  const maxResultChars = 100; // backend has the same limit
  const maxCompoundChars = 200; // backend has the same limit
  let childNode = children;
  const btnString = {
    Pending: record?.results_available === true ? "Review" : "Add",
    Ready: "Edit",
    "Ready-P": "View",
  };
  const buttonText = btnString[record?.status];

  const handleCompoundSelectChange = async () => {
    const currCompound = form.getFieldValue("compound");
    if (prevCompound === currCompound) {
      await form.validateFields(["compound"]);
      return; // If the compound name does not change, no need to make an API call
    }
    checkAndUpdateCompoundMap(currCompound, prevCompound);
    setPrevCompound(currCompound); // store the current compound
    handleCompoundChange(record.rowIndex); // make an API call
  };

  const duplicatedCompoundNameValidation = (_, value) => {
    if (value && compoundCounterMap.has(value.toLowerCase()) && compoundCounterMap.get(value.toLowerCase()) > 1) {
      return Promise.reject(new Error("Compound already exists"));
    }
    return Promise.resolve();
  };

  switch (dataIndex) {
    case "compound":
      childNode = (
        <Form.Item
          preserve={false}
          className="ChemPesticidesTable__FormItem"
          name={dataIndex}
          initialValue={record.compound}
          rules={[
            {
              required: record.editable,
              message: "Please select a compound",
            },
            { max: maxCompoundChars, message: `Max length is ${maxCompoundChars} characters.` },
            { validator: duplicatedCompoundNameValidation },
          ]}
        >
          {record.editable ? (
            <AutoComplete
              allowClear
              className="ChemPesticidesTableCell__Compound"
              disabled={loading}
              onSelect={() => handleCompoundSelectChange()}
              onBlur={() => handleCompoundSelectChange()}
              options={sampleDetail?.all_compounds.map((compoundItem) => ({ value: compoundItem.compound }))}
              filterOption={(inputValue, option) => option.value?.toLowerCase().startsWith(inputValue?.toLowerCase())}
            >
              <Input onPressEnter={() => handleCompoundSelectChange()} />
            </AutoComplete>
          ) : (<span>{record.compound}</span>)}
        </Form.Item>
      );
      break;
    case "compound_result":
      childNode = (
        <Form.Item
          preserve={false}
          className="ChemPesticidesTable__FormItem"
          name={dataIndex}
          initialValue={record.compound_result}
          rules={[
            {
              required: record.editable,
              message: "Please enter a valid result",
            },
            { max: maxResultChars, message: `Max length is ${maxResultChars} characters.` },
            ...(record.editable ? [mrlResultRule()] : []),
          ]}
        >
          {record.editable ? (
            <Input
              className="ChemPesticidesTableCell__CompoundResult"
            />
          ) : (<span>{record.compound_result}</span>)}
        </Form.Item>
      );
      break;
    case "action":
      childNode = (
        <Button
          className="ChemPesticidesTableCell__ActionBtn"
          onClick={() => openModal(record)}
        >
          {buttonText}
        </Button>
      );
      break;
    case "compound_row_action":
      childNode = record.editable ? (
        <Button
          className="ChemPesticidesTableCell__ActionBtn"
          onClick={() => handleDeleteRow(record.rowIndex)}
        >
          Delete
        </Button>
      ) : (<span>-</span>);
      break;
    case "status":
      childNode = (
        <span>
          {record.status}
        </span>
      );
      break;
    case "genista_id": {
      const overdueFlagStyle = TEST_OVERDUE_FLAGS[getTestOverdueFlag(parseInt(record.overdue_by))]?.textStyle;
      childNode = (
        <Tooltip
          placement="top"
          title=""
        >
          <span style={{
            ...overdueFlagStyle,
          lineHeight: "28px", // eslint-disable-line
          }}
          >
            {record.genista_id}
          </span>
        </Tooltip>
      );
    }
      break;
    default:
      if (record) {
        childNode = (
          <Form.Item
            preserve={false}
            className="ChemPesticidesTable__FormItem"
            name={dataIndex}
            initialValue={record?.[dataIndex] ?? ""}
            rules={[
              { max: maxResultChars, message: `Max length is ${maxResultChars} characters.` },
            ]}
          >
            {record.editable ? (
              <Input
                className="ChemPesticidesTableCell__CompoundResult"
              />
            ) : (<span>{record?.[dataIndex] ?? ""}</span>)}
          </Form.Item>
        );
      }

      break;
  }
  /** All other columns: show string value */
  return <td {...antdProps}>{childNode}</td>; // eslint-disable-line react/jsx-props-no-spreading
};

export default ChemPesticidesTableCell;
